/**
 * @fileoverview `/<npub>` -- one author's public pages.
 *
 * The cheap half of "how do I find things", and the half that needs no
 * judgement from us: it lists what one person published, in their own order of
 * publication, and we curate nothing. The expensive half -- a directory across
 * everyone -- is deferred, because it has to fan out over a follow graph and
 * carries a moderation surface this does not.
 *
 * WHY THIS ROUTE IS ALLOWED TO SAY "NOTHING HERE" when the page route never
 * says why a page did not load: everything listed here is public, and the
 * absence of public pages is not a secret. See the note in authorIndex.ts.
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { abbreviate, encodeProfile } from '@/services/nostr/identifiers';
import { pagePath } from '@/services/nostr/pageLinks';
import { loadAuthorIndex, type AuthorIndexLoad } from '@/services/pages/authorIndex';

export function AuthorRoute({ pubkey, hints }: { pubkey: string; hints: string[] }) {
  const [load, setLoad] = useState<AuthorIndexLoad | null>(null);
  // Serialised so the effect keys on the VALUE of the hints rather than the
  // identity of the array, which is rebuilt on every render of the parent.
  const hintKey = hints.join(',');

  useEffect(() => {
    let live = true;
    setLoad(null);
    void loadAuthorIndex(pubkey, hintKey ? hintKey.split(',') : []).then((result) => {
      if (live) setLoad(result);
    });
    return () => {
      live = false;
    };
  }, [pubkey, hintKey]);

  const label = load?.status === 'ready' ? load.profile.name : null;

  useEffect(() => {
    document.title = label ? `${label} — Pages` : 'Pages';
  }, [label]);

  if (load === null) {
    return (
      <div className="page-loading" role="status" aria-live="polite">
        Loading…
      </div>
    );
  }

  if (load.status === 'not-an-author') {
    return (
      <div className="page-unavailable" role="status">
        <h1>This is not an author</h1>
        <p>That address does not name anyone whose pages we could list.</p>
      </div>
    );
  }

  const { profile, pages } = load;
  const npub = encodeProfile(profile.pubkey);

  return (
    <div className="author-index">
      <header className="author-header">
        <h1 className="author-name">{profile.name ?? abbreviate(npub)}</h1>
        {profile.about && <p className="author-about">{profile.about}</p>}
        {/* The key is shown BESIDE a name, because a display name is
            self-declared and two people can pick the same one -- the key is
            what actually identifies the author. When there is no name the
            heading already IS the key, so repeating it would be noise. */}
        {profile.name && (
          <p className="author-key">
            <code>{abbreviate(npub)}</code>
          </p>
        )}
      </header>

      {pages.length === 0 ? (
        <p className="author-empty">No public pages by this author.</p>
      ) : (
        <ul className="author-pages">
          {pages.map((page) => (
            <li key={`${page.address.kind}:${page.address.identifier}`} className="author-page">
              <Link className="author-page-title" to={pagePath({ ...page.address, relays: hints })}>
                {page.title}
              </Link>
              {page.summary && <p className="author-page-summary">{page.summary}</p>}
              <p className="author-page-meta">
                <PublishedAt at={page.at} />
                {page.format === 'html' && <span className="author-page-format">HTML</span>}
              </p>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * A page's date, rendered in the reader's own locale.
 *
 * `dateTime` carries the machine-readable form, so the displayed text can be
 * local and informal without the value becoming ambiguous.
 */
function PublishedAt({ at }: { at: number }) {
  const date = new Date(at * 1000);
  if (Number.isNaN(date.getTime())) return null;
  return (
    <time dateTime={date.toISOString()}>
      {date.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
    </time>
  );
}
